import { useEffect, useMemo, useRef, useState } from 'react';
import { IconArrowLeft } from '@tabler/icons-react';
import { STAFF_EMAIL_DOMAIN, unwrapData } from '@wapharma/shared';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Button,
  Card,
  cx,
  IconButton,
  Input,
  Loader,
  Stack,
  Text,
  WP_ICON_NAV,
} from '@wapharma/design-system';
import { apiPost } from '../api';
import { getDisplayError, HttpError, parseFieldErrorsFromHttpError } from '../apiErrors';
import { AdminAuthLayout } from '../components/auth/AdminAuthLayout';
import { FormHeaderBand } from '../components/auth/FormHeaderBand';
import authStyles from '../components/auth/auth-layout.module.css';
import { PasswordInput } from '../components/PasswordInput';
import { StepSwitch } from '../components/StepSwitch';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { buildLoginWelcome, type LoginWelcomeInput } from '../utils/loginWelcome';
import { isAuthPublicReturnPath } from '../auth/publicPaths';
import { resolveApiMediaUrl } from '../lib/resolveApiMediaUrl';
import styles from './LoginPage.module.css';

type LoginStep = 'email' | 'password';

type LoginIdentity = LoginWelcomeInput;

function safeNextDestination(raw: string | null): string {
  if (!raw) return '/';
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return '/';
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//')) return '/';
  const noHash = decoded.split('#')[0] ?? '/';
  if (isAuthPublicReturnPath(noHash)) return '/';
  return noHash;
}

function staffLocalFromEmail(email: string): string {
  const v = email.trim();
  const at = v.indexOf('@');
  return (at >= 0 ? v.slice(0, at) : v).toLowerCase();
}

function toStaffLoginEmail(localPart: string): string {
  return `${localPart.trim().toLowerCase()}@${STAFF_EMAIL_DOMAIN}`;
}

function validateLoginUsername(value: string): string | undefined {
  const v = value.trim();
  if (!v) return "L'identifiant est requis.";
  if (v.includes('@') || /\s/.test(v) || !/^[a-zA-Z0-9._%+-]+$/.test(v)) {
    return 'Identifiant invalide.';
  }
  return undefined;
}

function validatePasswordField(value: string): string | undefined {
  if (!value.trim()) return 'Le mot de passe est requis.';
  return undefined;
}

export function LoginPage() {
  const { user, loading, login, mustChangePassword } = useAuth();
  const { showToast, clearToasts } = useToast();
  const [searchParams] = useSearchParams();
  const postLoginTo = useMemo(() => safeNextDestination(searchParams.get('next')), [searchParams]);
  const [step, setStep] = useState<LoginStep>('email');
  const [canFocusPassword, setCanFocusPassword] = useState(false);
  const [identity, setIdentity] = useState<LoginIdentity | null>(null);
  const [username, setUsername] = useState(import.meta.env.DEV ? 'admin' : '');
  const [password, setPassword] = useState('');
  const [serverFieldErrors, setServerFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const authInFlightRef = useRef(false);
  const year = new Date().getFullYear();

  const emailError = useMemo(() => {
    if (serverFieldErrors.email) return serverFieldErrors.email;
    if (!emailTouched && !submitAttempted) return undefined;
    return validateLoginUsername(username);
  }, [username, emailTouched, submitAttempted, serverFieldErrors.email]);

  const passwordError = useMemo(() => {
    if (serverFieldErrors.password) return serverFieldErrors.password;
    if (!passwordTouched && !submitAttempted) return undefined;
    return validatePasswordField(password);
  }, [password, passwordTouched, submitAttempted, serverFieldErrors.password]);

  const resolvedEmail = identity?.email ?? toStaffLoginEmail(username);
  const loginWelcome = useMemo(
    () => (identity ? buildLoginWelcome(identity) : null),
    [identity],
  );

  const stepIndex = step === 'password' ? 1 : 0;

  useEffect(() => {
    if (user) clearToasts();
  }, [user, clearToasts]);

  useEffect(() => () => clearToasts(), [clearToasts]);

  if (!loading && user) {
    if (mustChangePassword) {
      return <Navigate to="/force-password-change" replace />;
    }
    return <Navigate to={postLoginTo} replace />;
  }

  if (loading) {
    return (
      <div className={styles.bootFallback}>
        <Loader />
      </div>
    );
  }

  const resetToEmailStep = () => {
    clearToasts();
    setCanFocusPassword(false);
    setStep('email');
    setPassword('');
    setPasswordTouched(false);
    setSubmitAttempted(false);
    setServerFieldErrors({});
  };

  const handleStepTransitionEnd = () => {
    if (step === 'password') {
      setCanFocusPassword(true);
      return;
    }
    setIdentity(null);
  };

  const continueWithEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (authInFlightRef.current || pending) return;
    setSubmitAttempted(true);
    setServerFieldErrors({});

    const nextEmailError = validateLoginUsername(username);
    if (nextEmailError) return;

    const loginEmail = toStaffLoginEmail(username);
    authInFlightRef.current = true;
    setPending(true);
    void apiPost<{ data: LoginIdentity }>('/auth/login/identify', { email: loginEmail })
      .then((raw) => {
        const d = unwrapData(raw) as LoginIdentity;
        setIdentity({
          ...d,
          profilePictureUrl: resolveApiMediaUrl(d.profilePictureUrl) ?? d.profilePictureUrl,
        });
        setUsername(staffLocalFromEmail(d.email));
        setPassword('');
        setPasswordTouched(false);
        setSubmitAttempted(false);
        setServerFieldErrors({});
        setCanFocusPassword(false);
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        setStep('password');
      })
      .catch((er: unknown) => {
        const parsed = parseFieldErrorsFromHttpError(er);
        if (parsed?.email) {
          setServerFieldErrors({ email: parsed.email });
          return;
        }
        showToast(getDisplayError(er), { tone: 'error' });
      })
      .finally(() => {
        authInFlightRef.current = false;
        setPending(false);
      });
  };

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (authInFlightRef.current || pending) return;
    setSubmitAttempted(true);
    setServerFieldErrors({});
    const normalizedPassword = password.trim();

    const nextPasswordError = validatePasswordField(normalizedPassword);
    if (nextPasswordError) return;

    authInFlightRef.current = true;
    setPending(true);
    void login(resolvedEmail, normalizedPassword)
      .catch((er: unknown) => {
        const parsed = parseFieldErrorsFromHttpError(er);
        if (parsed) {
          setServerFieldErrors({
            email: parsed.email,
            password: parsed.password,
          });
          if (parsed.email) resetToEmailStep();
          if (parsed._form) {
            showToast(parsed._form, { tone: 'error' });
          }
          return;
        }
        const message = getDisplayError(er);
        if (er instanceof HttpError && er.status === 401) {
          setServerFieldErrors({ password: message });
        }
        showToast(message, { tone: 'error', autoDismissMs: 8000 });
      })
      .finally(() => {
        authInFlightRef.current = false;
        setPending(false);
      });
  };

  return (
    <AdminAuthLayout brandBody="Connectez-vous pour gérer le catalogue, le stock et les commandes.">
      <Stack gap="xl" className={authStyles.formStack}>
        <Card elevation="flat" padding="lg" className={authStyles.authFormCard}>
          <StepSwitch
            index={stepIndex}
            mode="panels"
            onTransitionEnd={handleStepTransitionEnd}
          >
            <Stack gap={0}>
              <FormHeaderBand>
                <div className={authStyles.formHeading}>
                  <Text ff="display" fw={600} component="p" className={authStyles.formTitle}>
                    Connexion
                  </Text>
                </div>
              </FormHeaderBand>

              <form onSubmit={continueWithEmail} noValidate className={authStyles.authStepBody}>
                <Stack gap="md">
                  <Input
                    label="Identifiant"
                    type="text"
                    name="username"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="username"
                    placeholder="prenom.nom"
                    value={username}
                    suffix={`@${STAFF_EMAIL_DOMAIN}`}
                    error={emailError}
                    tabIndex={step === 'email' ? undefined : -1}
                    onChange={(ev) => {
                      let next = ev.currentTarget.value;
                      const at = next.indexOf('@');
                      if (at >= 0) next = next.slice(0, at);
                      setUsername(next.replace(/\s/g, ''));
                      setEmailTouched(true);
                      if (serverFieldErrors.email) {
                        setServerFieldErrors((prev) => ({ ...prev, email: undefined }));
                      }
                    }}
                    onBlur={() => setEmailTouched(true)}
                  />
                  <Button type="submit" loading={pending} className={authStyles.submitBtn}>
                    Continuer
                  </Button>
                </Stack>
              </form>
            </Stack>

            <Stack gap={0}>
              {identity ? (
                <>
                  <FormHeaderBand>
                    <div className={cx(authStyles.formHeading, styles.passwordHeading)}>
                      <IconButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        borderless
                        className={styles.changeEmailBtn}
                        aria-label="Utiliser un autre identifiant"
                        tabIndex={step === 'password' ? undefined : -1}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.preventDefault();
                          resetToEmailStep();
                        }}
                      >
                        <IconArrowLeft {...WP_ICON_NAV} aria-hidden />
                      </IconButton>
                      <Avatar
                        label={loginWelcome?.displayName ?? identity.email}
                        src={identity.profilePictureUrl}
                        size="md"
                        className={styles.identityAvatar}
                      />
                      <div className={styles.passwordHeadingText}>
                        <Text ff="display" fw={600} component="p" className={authStyles.formTitle}>
                          {loginWelcome ? (
                            <>
                              <span className={styles.welcomeLead}>
                                {loginWelcome.greetingLead},{' '}
                              </span>
                              <span className={styles.welcomeName}>
                                {loginWelcome.emphasizedName}
                              </span>
                            </>
                          ) : (
                            'Connexion'
                          )}
                        </Text>
                        <p className={styles.welcomeEmail}>{identity.email}</p>
                      </div>
                    </div>
                  </FormHeaderBand>

                  <Stack gap="md" className={authStyles.authStepBody}>
                    <form onSubmit={submitPassword} noValidate>
                      <Stack gap="md">
                        <PasswordInput
                          label="Mot de passe"
                          name="password"
                          autoComplete="current-password"
                          placeholder="••••••••"
                          value={password}
                          error={passwordError}
                          autoFocus={canFocusPassword && step === 'password'}
                          tabIndex={step === 'password' ? undefined : -1}
                          onChange={(ev) => {
                            setPassword(ev.target.value);
                            setPasswordTouched(true);
                            if (serverFieldErrors.password) {
                              setServerFieldErrors((prev) => ({ ...prev, password: undefined }));
                            }
                          }}
                          onBlur={() => setPasswordTouched(true)}
                        />
                        <Text
                          size="sm"
                          ta="right"
                          ff="body"
                          tabIndex={step === 'password' ? undefined : -1}
                        >
                          <Link
                            to="/forgot-password"
                            className={authStyles.authNavLink}
                            tabIndex={step === 'password' ? undefined : -1}
                          >
                            Mot de passe oublié ?
                          </Link>
                        </Text>
                        <Button type="submit" loading={pending} className={authStyles.submitBtn}>
                          Se connecter
                        </Button>
                      </Stack>
                    </form>
                  </Stack>
                </>
              ) : null}
            </Stack>
          </StepSwitch>
        </Card>

        <Text size="xs" c="dimmed" ta="center" ff="body" className={authStyles.mobileFooter}>
          © {year} W.A. Pharma · Réservé au personnel autorisé
        </Text>
      </Stack>
    </AdminAuthLayout>
  );
}
